import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { useContext, useEffect, useState } from "react";
import {
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Upload } from "antd";

import "../PubScaleAds/styles.css";

import { Context } from "../../context";
import { Fetch, Post, Put, Delete } from "../../fetch";

const { Column } = Table;
const { Option } = Select;
const { confirm } = Modal;

/**
 * vCommission Offers — affiliate-network (CPA/CPS) campaigns shown on the
 * MiniPix Tasks tab next to the PubScale offers. Stored in `tasks_config`
 * (kind = vcommission_offer) via setu /v4/tasks-config/vc-offers.
 *
 * Flow: app tap → setu builds the tracking link with p1 (AG user) / p2
 * (click id) / p3 (campaign id) → user converts on the advertiser → vCommission
 * fires the common postback into AG → AG reads THIS doc's goal rules and
 * credits coins. Reward rules here are the money — edit with care.
 */

const CATEGORIES = [
  { value: "non_gaming", label: "Non Gaming", color: "blue" },
  { value: "gaming", label: "Gaming", color: "purple" },
  { value: "survey", label: "Survey", color: "green" },
];
const REWARD_MODES = [
  { value: "share_pct", label: "% of vCommission payout" },
  { value: "fixed_coins", label: "Fixed coins" },
];

const categoryLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || v;
const categoryColor = (v) => CATEGORIES.find((c) => c.value === v)?.color || "default";
const rewardLabel = (r) =>
  !r ? "—" : r.reward_mode === "fixed_coins" ? `${r.value} coins` : `${r.value}% of payout`;

const VcommissionOffers = () => {
  const { token, apiUrl } = useContext(Context);

  const [offers, setOffers] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [modalType, setModalType] = useState("Add");
  const [editingId, setEditingId] = useState(null);
  const [counter, setCounter] = useState(0);

  const [form] = Form.useForm();

  useEffect(() => {
    Fetch(`${apiUrl}/v4/tasks-config/vc-offers`, token, (result) => {
      if (result && result.success !== false) setOffers(result.offers || []);
    });
  }, [apiUrl, token, counter]);

  const showAddModal = () => {
    setModalType("Add");
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      enabled: true,
      display_order: 0,
      category: "non_gaming",
      goals: [],
      default_mode: "global",
      hold_days: 0,
      hold_hours: 0,
    });
    setModalVisible(true);
  };

  const showEditModal = (record) => {
    setModalType("Edit");
    setEditingId(record.id);
    form.resetFields();
    form.setFieldsValue({
      offer_id: record.offer_id,
      name: record.name,
      category: record.category,
      enabled: record.enabled ?? false,
      display_order: record.display_order ?? 0,
      icon_url: record.icon_url || "",
      description: record.description || "",
      coins: record.coins ?? null,
      trk_url: record.trk_url || "",
      goals: (record.goals || []).map((g) => ({
        title: g.title,
        instructions: g.instructions || "",
        coins: g.coins ?? null,
        goal_id: g.goal_id || "",
        reward_mode: g.reward_mode || "share_pct",
        value: g.value ?? null,
      })),
      default_mode: record.default_reward ? record.default_reward.reward_mode : "global",
      default_value: record.default_reward ? record.default_reward.value : null,
      hold_days: record.hold_days ?? 0,
      hold_hours: record.hold_hours ?? 0,
    });
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingId(null);
    setCreativeS3Path(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    let f;
    try {
      f = await form.validateFields();
    } catch (err) {
      notification.error({
        message: `${modalType} Offer Error`,
        description: "Required fields are incorrect/missing",
      });
      return;
    }
    const payload = {
      offer_id: f.offer_id,
      name: f.name,
      category: f.category,
      enabled: f.enabled ?? false,
      display_order: f.display_order ?? 0,
      icon_url: (f.icon_url || "").trim() || null,
      description: (f.description || "").trim() || null,
      coins: f.coins ?? null,
      trk_url: (f.trk_url || "").trim(),
      goals: (f.goals || []).map((g, i) => ({
        title: g.title,
        instructions: (g.instructions || "").trim() || null,
        coins: g.coins ?? null,
        goal_id: (g.goal_id || "").trim() || null,
        reward_mode: g.reward_mode || "share_pct",
        value: Number(g.value) || 0,
        order: i,
      })),
      default_reward:
        f.default_mode && f.default_mode !== "global"
          ? { reward_mode: f.default_mode, value: Number(f.default_value) || 0 }
          : null,
      hold_days: f.hold_days ?? 0,
      hold_hours: f.hold_hours ?? 0,
    };

    setConfirmLoading(true);
    const onErr = (verb) => (errStatus) => {
      setConfirmLoading(false);
      notification.error({
        message: `Error ${verb} offer`,
        description:
          errStatus === "409"
            ? `A vCommission offer with campaign id ${payload.offer_id} already exists`
            : "Check console for more details",
      });
    };

    if (modalType === "Edit") {
      Put(
        `${apiUrl}/v4/tasks-config/vc-offers/${editingId}`,
        token,
        payload,
        (result) => {
          setConfirmLoading(false);
          if (result.success) {
            notification.success({ message: "Success", description: "Offer updated" });
            closeModal();
            setCounter((c) => c + 1);
          } else {
            notification.error({
              message: "Error updating offer",
              description: (result.detail && result.detail.message) || result.message || "Check console",
            });
          }
        },
        onErr("updating")
      );
      return;
    }
    Post(
      `${apiUrl}/v4/tasks-config/vc-offers`,
      token,
      payload,
      () => {
        setConfirmLoading(false);
        notification.success({ message: "Success", description: "Offer created" });
        closeModal();
        setCounter((c) => c + 1);
      },
      onErr("creating")
    );
  };

  const handleEnabledToggle = (record, checked) => {
    setOffers((prev) => prev.map((o) => (o.id === record.id ? { ...o, enabled: checked } : o)));
    Put(
      `${apiUrl}/v4/tasks-config/vc-offers/${record.id}`,
      token,
      { enabled: checked },
      (result) => {
        if (!result.success) setCounter((c) => c + 1);
      },
      () => setCounter((c) => c + 1)
    );
  };

  const confirmDelete = (record) => {
    confirm({
      title: `Delete vCommission offer "${record.name}"?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        Delete(
          `${apiUrl}/v4/tasks-config/vc-offers/${record.id}`,
          token,
          (result) => {
            if (result.success) {
              notification.success({ message: "Success", description: "Offer deleted" });
              setCounter((c) => c + 1);
            } else {
              notification.error({ message: "Error deleting offer", description: result.message || "Check console" });
            }
          },
          () => notification.error({ message: "Error deleting offer", description: "Check console" })
        );
      },
    });
  };

  const defaultMode = Form.useWatch("default_mode", form);
  const iconUrl = Form.useWatch("icon_url", form);
  const campaignIdValue = Form.useWatch("offer_id", form);
  const [creativeS3Path, setCreativeS3Path] = useState(null);
  const [uploadingCreative, setUploadingCreative] = useState(false);

  // Derive the S3 location from a CDN url produced by our upload endpoint
  // (https://<cdn>/offers/vcommission/<campaign>/<uuid>.<ext>).
  const s3PathFor = (url) => {
    if (!url) return null;
    const m = String(url).match(/\/(offers\/vcommission\/[^?#]+)/);
    return m ? `s3://minipix-content-ott/${m[1]}` : null;
  };

  const beforeCreativeUpload = (file) => {
    const okType = ["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type);
    if (!okType) notification.error({ message: "Creative must be jpeg/png/webp/gif" });
    const okSize = file.size / 1024 / 1024 <= 2;
    if (!okSize) notification.error({ message: "Creative must be ≤ 2MB" });
    return (okType && okSize) || Upload.LIST_IGNORE;
  };

  const onCreativeChange = (info) => {
    if (info.file.status === "uploading") setUploadingCreative(true);
    if (info.file.status === "done") {
      setUploadingCreative(false);
      const res = info.file.response || {};
      if (res.success && res.url) {
        form.setFieldsValue({ icon_url: res.url });
        setCreativeS3Path(res.s3_path || s3PathFor(res.url));
        notification.success({ message: "Creative uploaded", description: res.s3_path || res.url });
      } else {
        notification.error({ message: "Upload failed", description: (res.detail && res.detail.message) || "Check console" });
      }
    }
    if (info.file.status === "error") {
      setUploadingCreative(false);
      notification.error({ message: "Upload failed", description: "Check console for more details" });
    }
  };

  return (
    <div className="pubscale-ads-wrapper">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="vCommission affiliate offers (Ajio, MobileXpression, …)"
        description="Shown on the MiniPix Tasks tab beside PubScale offers. On tap, setu appends p1 (AG user) / p2 (click id) / p3 (campaign id) to the tracking link. vCommission's common postback lands on Affiliate Guru, which reads the goal rules below to credit coins. Only app builds ≥ Task Config → vCommission min versionCode see these offers."
      />
      <div className="pubscale-ads-toolbar">
        <Typography.Title level={5} style={{ margin: 0 }}>vCommission Offers</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
          Add Offer
        </Button>
      </div>
      <Table dataSource={offers} rowKey="id" style={{ marginTop: 12 }}>
        <Column title="Campaign ID" dataIndex="offer_id" key="offer_id" align="center" />
        <Column title="Name" dataIndex="name" key="name" align="center" />
        <Column
          title="Category"
          dataIndex="category"
          key="category"
          align="center"
          render={(c) => <Tag color={categoryColor(c)}>{categoryLabel(c)}</Tag>}
        />
        <Column title="Display coins" dataIndex="coins" key="coins" align="center" render={(v) => v ?? "—"} />
        <Column
          title="Goal rules"
          key="goals"
          align="center"
          render={(text, r) => (
            <Space direction="vertical" size={0}>
              {(r.goals || []).map((g, i) => (
                <span key={i}>{g.title}: {rewardLabel(g)}</span>
              ))}
              <span style={{ color: "#888" }}>default: {r.default_reward ? rewardLabel(r.default_reward) : "AG global %"}</span>
            </Space>
          )}
        />
        <Column
          title="Hold"
          key="hold"
          align="center"
          render={(text, r) => `${r.hold_days || 0}d ${r.hold_hours || 0}h`}
        />
        <Column
          title="Enabled"
          dataIndex="enabled"
          key="enabled"
          align="center"
          render={(enabled, record) => (
            <Switch checked={Boolean(enabled)} onChange={(checked) => handleEnabledToggle(record, checked)} />
          )}
        />
        <Column title="Order" dataIndex="display_order" key="display_order" align="center" />
        <Column
          title="Actions"
          key="actions"
          align="center"
          render={(text, record) => (
            <Space size="middle">
              <Typography.Link onClick={() => showEditModal(record)}>Edit</Typography.Link>
              <Typography.Link onClick={() => confirmDelete(record)}>Delete</Typography.Link>
            </Space>
          )}
        />
      </Table>

      <Modal
        title={`${modalType} vCommission Offer`}
        visible={modalVisible}
        onOk={handleSubmit}
        okText="Submit"
        onCancel={closeModal}
        confirmLoading={confirmLoading}
        destroyOnClose={true}
        maskClosable={false}
        width={760}
      >
        <Form form={form} layout="vertical">
          <Space size="large" style={{ display: "flex" }} align="start">
            <Form.Item
              name="offer_id"
              label="Campaign ID"
              rules={[{ required: true, message: "Campaign id is required" }]}
              tooltip="vCommission short campaign id (campaign_id in the tracking link, camp_id in postbacks). E.g. Ajio = 10364"
            >
              <InputNumber min={0} precision={0} placeholder="10364" style={{ width: 160 }} />
            </Form.Item>
            <Form.Item name="name" label="Name" rules={[{ required: true, message: "Name is required" }]} style={{ flex: 1 }}>
              <Input placeholder="Ajio — Shop & Earn" style={{ width: 320 }} />
            </Form.Item>
            <Form.Item name="category" label="Category" rules={[{ required: true }]}>
              <Select style={{ width: 150 }}>
                {CATEGORIES.map((c) => (
                  <Option key={c.value} value={c.value}>{c.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Space>

          <Form.Item
            name="trk_url"
            label="Tracking link"
            rules={[
              { required: true, message: "Tracking link is required" },
              { pattern: /^https?:\/\//, message: "Must start with http(s)://" },
            ]}
            tooltip="Copy the campaign's Tracking Link as-is. setu appends p1/p2/p3 per tap — no 'Add Tracking Param' needed."
          >
            <Input placeholder="https://track.vcommission.com/click?campaign_id=10364&pub_id=132515" />
          </Form.Item>

          <Space size="large" style={{ display: "flex" }} align="start">
            <Form.Item
              label="Icon / creative"
              tooltip="Uploads to S3 via setu: s3://minipix-content-ott/offers/vcommission/<campaign id>/<uuid>.<ext>, served from the CDN. Square 200×200 or 300×250 works best on the card."
              style={{ flex: 1 }}
            >
              <Space align="start">
                <Upload
                  name="file"
                  accept="image/*"
                  showUploadList={false}
                  action={`${apiUrl}/v4/tasks-config/vc-offers/creative?campaign_id=${campaignIdValue || ""}`}
                  headers={{ authorization: `Bearer ${token}` }}
                  beforeUpload={beforeCreativeUpload}
                  onChange={onCreativeChange}
                >
                  <Button icon={<UploadOutlined />} loading={uploadingCreative}>
                    {iconUrl ? "Replace creative" : "Upload creative"}
                  </Button>
                </Upload>
                {iconUrl ? (
                  <img src={iconUrl} alt="creative" style={{ height: 64, borderRadius: 8, border: "1px solid #eee" }} />
                ) : null}
              </Space>
              <Form.Item name="icon_url" noStyle>
                <Input
                  placeholder="…or paste an existing CDN URL"
                  style={{ width: 420, marginTop: 8 }}
                  onChange={(e) => setCreativeS3Path(s3PathFor(e.target.value))}
                />
              </Form.Item>
              {(creativeS3Path || s3PathFor(iconUrl)) ? (
                <Typography.Text type="secondary" copyable style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                  {creativeS3Path || s3PathFor(iconUrl)}
                </Typography.Text>
              ) : null}
            </Form.Item>
            <Form.Item name="coins" label="Display coins" tooltip="What the card advertises ('Earn up to N coins'). Not the credited amount.">
              <InputNumber min={0} precision={0} style={{ width: 130 }} />
            </Form.Item>
          </Space>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Shop on Ajio via MiniPix and earn coins on every order." />
          </Form.Item>

          <Typography.Text strong>Goal rules</Typography.Text>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Mirror the campaign's "Payout &amp; Goals" table. Matched by goal_id first, then by title (case-insensitive contains).
            "% of payout" = share of the ₹ payout vCommission reports; "Fixed coins" ignores the payout.
          </Typography.Paragraph>
          <Form.List name="goals">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Space key={key} align="baseline" style={{ display: "flex", marginBottom: 4 }} wrap>
                    <Form.Item {...rest} name={[name, "title"]} rules={[{ required: true, message: "title" }]}>
                      <Input placeholder="Goal title (e.g. New user)" style={{ width: 200 }} />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, "goal_id"]}>
                      <Input placeholder="goal_id (optional)" style={{ width: 130 }} />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, "reward_mode"]} initialValue="share_pct">
                      <Select style={{ width: 200 }}>
                        {REWARD_MODES.map((m) => (
                          <Option key={m.value} value={m.value}>{m.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item {...rest} name={[name, "value"]} rules={[{ required: true, message: "value" }]}>
                      <InputNumber min={0} step={1} placeholder="value" style={{ width: 90 }} />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, "coins"]}>
                      <InputNumber min={0} precision={0} placeholder="show coins" style={{ width: 110 }} />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add({ reward_mode: "share_pct" })} icon={<PlusOutlined />}>
                    Add goal rule
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Space size="large" style={{ display: "flex" }} align="start">
            <Form.Item name="default_mode" label="Default rule (no goal matched)" initialValue="global">
              <Select style={{ width: 240 }}>
                <Option value="global">AG global default %</Option>
                {REWARD_MODES.map((m) => (
                  <Option key={m.value} value={m.value}>{m.label}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="default_value" label={defaultMode === "fixed_coins" ? "Coins" : "% of payout"}>
              <InputNumber min={0} disabled={!defaultMode || defaultMode === "global"} style={{ width: 130 }} />
            </Form.Item>
            <Form.Item name="hold_days" label="Hold days" tooltip="Earning hold before withdrawal — vCommission can reverse sales for weeks.">
              <InputNumber min={0} precision={0} style={{ width: 90 }} />
            </Form.Item>
            <Form.Item name="hold_hours" label="Hold hours">
              <InputNumber min={0} max={23} precision={0} style={{ width: 90 }} />
            </Form.Item>
          </Space>

          <Space size="large">
            <Form.Item name="enabled" label="Enabled" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="display_order" label="Display order">
              <InputNumber min={0} precision={0} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default VcommissionOffers;
